import { supabase } from '@/integrations/supabase/client';
import i18n from '@/i18n';
import { AI_MODEL_COLUMNS } from './ai-model-columns';

type ModelCapabilitySignal = {
  capabilities?: unknown;
  model_config?: unknown;
} | null | undefined;

/**
 * THE CONVENTION for what makes a model fit to serve as a conversational assistant's
 * brain: `capabilities.chat === true`. `ai_models` has no `model_type` column, and the
 * default listing order (provider, then model_name) can surface a non-chat model
 * (an image-generation model, for instance) before any language model — a screen that
 * takes the list unfiltered can silently offer it as an assistant's brain.
 *
 * Every caller that offers a model for that purpose must go through this function (or
 * the `onlyConversational` option on `useModels()` / `getActiveModels()` /
 * `getAllModels()` below) instead of re-deriving its own filter. Two independent ad hoc
 * filters existed before this one — `capabilities.chat` in the space wizard's assistant
 * step, `model_config.model_type` in the model playground — and each caught rows the
 * other missed, which is exactly the failure this function exists to close.
 *
 * `capabilities.chat` is not the only shape on record, though (verified live
 * 2026-08-24): some catalog-synced language models (the `gpt-5.4` family) carry no
 * `chat` key at all and describe themselves only through `model_config.model_type`.
 * A model is conversational when `capabilities.chat` says so explicitly, or — when it
 * is silent on the question — when the catalog classified it as `language`,
 * `reasoning` or `code`.
 */
export function isConversationalModel(model: ModelCapabilitySignal): boolean {
  const capabilities = (model?.capabilities ?? {}) as Record<string, unknown>;
  if (capabilities.chat === true) return true;
  if (capabilities.chat === false) return false;

  const modelType = (model?.model_config as { model_type?: string } | null | undefined)?.model_type;
  return modelType === 'language' || modelType === 'reasoning' || modelType === 'code';
}

export type ModelPurpose = 'chatVision' | 'chat' | 'embedding' | 'tts' | 'stt' | 'image' | 'other';

/**
 * Derives a single, human-facing purpose label from a model's real `capabilities` /
 * `model_config` / `has_vision` fields — it never invents a capability that is not
 * present in the data. Used by the models management table to say what each model is
 * for, since `ai_models` itself has no such column.
 */
export function getModelPurpose(model: ModelCapabilitySignal & { has_vision?: boolean }): ModelPurpose {
  const capabilities = (model?.capabilities ?? {}) as Record<string, unknown>;
  const modelType = (model?.model_config as { model_type?: string } | null | undefined)?.model_type;

  if (isConversationalModel(model)) {
    const hasVision = model?.has_vision === true || capabilities.vision === true;
    return hasVision ? 'chatVision' : 'chat';
  }
  if (capabilities.embedding === true || modelType === 'embedding') return 'embedding';
  if (capabilities.tts === true || modelType === 'tts') return 'tts';
  if (modelType === 'stt') return 'stt';
  if (modelType === 'image' || capabilities.image === true) return 'image';
  return 'other';
}

export class LLMProviderService {
  private defaultModel: unknown = null;

  static async getAvailableProviders() {
    try {
      const { data: models, error } = await supabase
        .from('ai_models')
        .select('provider')
        .eq('is_active', true);
      
      if (error) throw error;
      
      // Get unique providers
      const providers = [...new Set(models?.map(m => m.provider) || [])];
      return providers;
    } catch (error) {
      console.error('Error fetching providers:', error);
      return [];
    }
  }

  static async getProviderModels(provider: string) {
    try {
      const { data: models, error } = await supabase
        .from('ai_models')
        .select(AI_MODEL_COLUMNS)
        .eq('provider', provider)
        .eq('is_active', true);
      
      if (error) throw error;
      return models || [];
    } catch (error) {
      console.error('Error fetching provider models:', error);
      return [];
    }
  }

  // V-i18n: `message` stays as the English fallback — this is a plain service class, not a
  // component or hook, so it cannot call `t()`. `messageKey` is the stable identifier meant for
  // the caller to resolve via `t()`; see modules/ai-management/components/ModelConfigDialog.tsx
  // (outside this lote), which today shows `result.message` straight through in
  // `t('modelConfigDialog.toasts.testFailed', { message: result.message })` — that call needs to
  // switch to `t(result.messageKey ?? 'modelConfigDialog.toasts.unknownError')` for the toast to
  // translate correctly.
  static async testModelConnection(modelId: string) {
    try {
      const { data, error } = await supabase.functions.invoke('test-connection', {
        body: { action: 'ai_model', model_id: modelId }
      });

      if (error) throw error;
      return data || { success: false, message: i18n.t('aiAssistant:modelConnectionTest.unknownError'), messageKey: 'modelConfigDialog.toasts.unknownError' };
    } catch (error) {
      console.error('Error testing model connection:', error);
      return { success: false, message: i18n.t('aiAssistant:modelConnectionTest.connectionTestFailed'), messageKey: 'modelConfigDialog.toasts.connectionTestFailed' };
    }
  }

  static async getModelUsageStats(modelId: string, tenantId?: string) {
    // Placeholder implementation
    return {
      total_requests: 0,
      total_tokens: 0,
      average_response_time: 0,
      error_rate: 0
    };
  }

  async getDefaultModel(tenantId?: string) {
    if (this.defaultModel) return this.defaultModel;

    try {
      const { data: models, error } = await supabase
        .from('ai_models')
        .select(AI_MODEL_COLUMNS)
        .eq('is_default', true)
        .eq('is_active', true)
        .limit(1);
      
      if (error) throw error;
      return models?.[0] || null;
    } catch (error) {
      console.error('Error getting default model:', error);
      return null;
    }
  }

  static async setDefaultModel(modelId: string, tenantId?: string) {
    try {
      // First, unset all default models
      const { error: unsetError } = await supabase
        .from('ai_models')
        .update({ is_default: false })
        .eq('is_default', true);
      
      if (unsetError) throw unsetError;
      
      // Set the new default model
      const { error: setError } = await supabase
        .from('ai_models')
        .update({ is_default: true })
        .eq('id', modelId);
      
      if (setError) throw setError;
      return true;
    } catch (error) {
      console.error('Error setting default model:', error);
      return false;
    }
  }

  getModel(modelId: string) {
    // This should be async but keeping interface for now
    return null;
  }

  async getTenantModels(tenantId: string) {
    try {
      const { data: permissions, error } = await supabase
        .from('tenant_ai_model_permissions')
        .select(`
          *,
          ai_models (${AI_MODEL_COLUMNS})
        `)
        .eq('tenant_id', tenantId)
        .eq('is_active', true);
      
      if (error) throw error;
      return permissions?.map(p => p.ai_models).filter(Boolean) || [];
    } catch (error) {
      console.error('Error fetching tenant models:', error);
      return [];
    }
  }

  async validateModelAccess(modelId: string, tenantId: string) {
    try {
      const { data: permission, error } = await supabase
        .from('tenant_ai_model_permissions')
        .select('*')
        .eq('tenant_id', tenantId)
        .eq('platform_model_id', modelId)
        .eq('is_active', true)
        .limit(1);
      
      if (error) throw error;
      return !!permission?.[0];
    } catch (error) {
      console.error('Error validating model access:', error);
      return false;
    }
  }

  static async getAllModels() {
    try {
      const { data: models, error } = await supabase
        .from('ai_models')
        .select(AI_MODEL_COLUMNS)
        .order('provider', { ascending: true })
        .order('model_name', { ascending: true });
      
      if (error) throw error;
      return models || [];
    } catch (error) {
      console.error('Error fetching all models:', error);
      return [];
    }
  }

  static async getActiveModels() {
    try {
      const { data: models, error } = await supabase
        .from('ai_models')
        .select(AI_MODEL_COLUMNS)
        .eq('is_active', true)
        .order('provider', { ascending: true })
        .order('model_name', { ascending: true });
      
      if (error) throw error;
      return models || [];
    } catch (error) {
      console.error('Error fetching active models:', error);
      return [];
    }
  }

  static async generateSmartReplies(messageContent: string, context?: Record<string, unknown>) {
    // Placeholder implementation
    return [];
  }

  static async analyzeMessage(messageContent: string, context?: Record<string, unknown>) {
    // Placeholder implementation
    return { sentiment: 'neutral', intent: 'unknown', confidence: 0 };
  }

  static async generateResponse(prompt: string, context?: Record<string, unknown>) {
    // Placeholder implementation
    return { content: '', usage: { tokens: 0 } };
  }

  static async updateModel(modelId: string, updates: Record<string, unknown>) {
    try {
      const { error } = await supabase
        .from('ai_models')
        .update(updates)
        .eq('id', modelId);
      
      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error updating model:', error);
      return false;
    }
  }

  static async toggleModelStatus(modelId: string, isActive: boolean) {
    try {
      const { error } = await supabase
        .from('ai_models')
        .update({ is_active: isActive })
        .eq('id', modelId);
      
      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error toggling model status:', error);
      return false;
    }
  }

  static async deleteModel(modelId: string) {
    try {
      const { error } = await supabase
        .from('ai_models')
        .delete()
        .eq('id', modelId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting model:', error);
      return false;
    }
  }

  static async fetchCatalog() {
    try {
      const { data, error } = await supabase.functions.invoke('ai-model-catalog', { body: { action: 'sync' } });
      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || 'Catalog sync failed');
      return data;
    } catch (error) {
      console.error('Error fetching catalog:', error);
      throw error;
    }
  }

  static async addModel(model: {
    provider: string;
    model_name: string;
    context_window?: number;
    pricing?: { input_tokens: number; output_tokens: number };
    capabilities?: Record<string, boolean>;
    has_vision?: boolean;
    model_config?: Record<string, unknown>;
    access_tier?: 'basic' | 'pro' | 'enterprise';
  }) {
    try {
      // Auto-detect tokens_param for OpenAI models
      // Newer models use max_completion_tokens; legacy models use max_tokens
      const config = { ...(model.model_config || {}) };
      if (model.provider.toLowerCase() === 'openai' && !config.tokens_param) {
        const name = model.model_name.toLowerCase();
        // Legacy models that still use max_tokens
        const usesLegacyMaxTokens = /^(gpt-4o|gpt-4-|gpt-3|davinci|babbage|tts|whisper|dall-e|text-embedding)/.test(name);
        if (!usesLegacyMaxTokens) {
          config.tokens_param = 'max_completion_tokens';
        }
      }

      const { data, error } = await supabase
        .from('ai_models')
        .insert({
          provider: model.provider.toLowerCase(),
          model_name: model.model_name,
          context_window: model.context_window || 4096,
          pricing: model.pricing || { input_tokens: 0, output_tokens: 0 },
          capabilities: model.capabilities || { chat: true, embedding: false, completion: false, fine_tuning: false },
          has_vision: model.has_vision || false,
          model_config: config,
          access_tier: model.access_tier || 'basic',
          is_active: false,
          is_default: false,
          status: 'active',
        })
        .select(AI_MODEL_COLUMNS)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error adding model:', error);
      throw error;
    }
  }

  static async syncAllPermissions() {
    try {
      const { data, error } = await supabase.rpc('sync_all_tenant_model_permissions');
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error syncing permissions:', error);
      throw error;
    }
  }
}

export const llmProviderService = new LLMProviderService();
